"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthService = exports.AuthServiceType = void 0;
const auth0_1 = require("./auth0");
const cognito_1 = require("./cognito");
const okta_1 = require("./okta");
var AuthServiceType;
(function (AuthServiceType) {
    AuthServiceType["COGNITO"] = "COGNITO";
    AuthServiceType["OKTA"] = "OKTA";
    AuthServiceType["AUTH0"] = "AUTH0";
})(AuthServiceType || (exports.AuthServiceType = AuthServiceType = {}));
class AuthService {
    /**
     * Creates the auth service
     * @param sskyCognitoConfig configuration for cognito
     * @param sskyOktaConfig configuration for okta
     * @param sskyAuth0Config configuration for auth0
     * @param getServiceTypeForUser callback to get user's service type and last login
     */
    constructor(sskyCognitoConfig, sskyOktaConfig, sskyAuth0Config, getServiceTypeForUser) {
        if (sskyCognitoConfig) {
            this.cognitoService = new cognito_1.SskyCognitoService(sskyCognitoConfig);
        }
        if (sskyOktaConfig) {
            this.oktaService = new okta_1.SskyOktaService(sskyOktaConfig);
        }
        if (sskyAuth0Config) {
            this.auth0Service = new auth0_1.SskyAuth0Service(sskyAuth0Config);
        }
        this.getServiceTypeForUser = getServiceTypeForUser;
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            // 1. Check cognito
            if (this.cognitoService) {
                yield this.cognitoService.init();
                const cognitoUsername = yield this.getExistingUserName(this.cognitoService);
                if (cognitoUsername) {
                    // User is logged-in to Cognito, checking the user should use Cognito
                    yield this.updateUserAuthServiceType(cognitoUsername);
                }
            }
            // 2. Check if user is a Cognito user
            if (this.userAuthServiceType === AuthServiceType.COGNITO) {
                // No need to check Auth0 or Okta
                return;
            }
            // 3. Check Auth0
            if (this.auth0Service) {
                yield this.auth0Service.init();
                const auth0Username = yield this.getExistingUserName(this.auth0Service);
                if (auth0Username) {
                    yield this.updateUserAuthServiceType(auth0Username);
                }
            }
            // 4. Check if user is an Auth0 user
            if (this.userAuthServiceType === AuthServiceType.AUTH0) {
                // No need to check Okta
                return;
            }
            // 5. Check Okta (this is done only if Cognito has no session, or the session is for a user that is not a Cognito user.
            if (this.oktaService) {
                yield this.oktaService.init();
                const oktaUsername = yield this.getExistingUserName(this.oktaService);
                if (oktaUsername) {
                    yield this.updateUserAuthServiceType(oktaUsername);
                }
            }
        });
    }
    //////////////////////////
    // AuthServiceInterface //
    //////////////////////////
    isFirstLogin(user) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.userAuthServiceType) {
                yield this.updateUserAuthServiceType(user);
            }
            return this.userFirstLogin;
        });
    }
    isUserDeactivated(user_1) {
        return __awaiter(this, arguments, void 0, function* (user, force = false) {
            if (force || !this.userAuthServiceType) {
                yield this.updateUserAuthServiceType(user);
            }
            return this.userDeactivated;
        });
    }
    login(user, password) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            return (_a = this.getAuthService()) === null || _a === void 0 ? void 0 : _a.login(user, password);
        });
    }
    loginWithRedirect(user) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            return (_a = this.getAuthService()) === null || _a === void 0 ? void 0 : _a.loginWithRedirect(user);
        });
    }
    loginWithPopup(user) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            return (_a = this.getAuthService()) === null || _a === void 0 ? void 0 : _a.loginWithPopup(user);
        });
    }
    logout() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            return (_a = this.getAuthService()) === null || _a === void 0 ? void 0 : _a.logout();
        });
    }
    isUserManaged(user, force) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (force || !this.userAuthServiceType) {
                yield this.updateUserAuthServiceType(user);
            }
            return (_a = this.getAuthService()) === null || _a === void 0 ? void 0 : _a.isUserManaged(user, force);
        });
    }
    getUserAccessToken() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                return yield ((_a = this.getAuthService()) === null || _a === void 0 ? void 0 : _a.getUserAccessToken());
            }
            catch (e) {
                return null;
            }
        });
    }
    getUserIdToken() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            return (_a = this.getAuthService()) === null || _a === void 0 ? void 0 : _a.getUserIdToken();
        });
    }
    getUserName() {
        var _a;
        return (_a = this.getAuthService()) === null || _a === void 0 ? void 0 : _a.getUserName();
    }
    initiateForgotPassword(user) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            yield this.updateUserAuthServiceType(user);
            return (_a = this.getAuthService()) === null || _a === void 0 ? void 0 : _a.initiateForgotPassword(user);
        });
    }
    confirmForgotPassword(username, verificationCode, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            return (_a = this.getAuthService()) === null || _a === void 0 ? void 0 : _a.confirmForgotPassword(username, verificationCode, newPassword);
        });
    }
    changePassword(user, oldPassword, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            return (_a = this.getAuthService()) === null || _a === void 0 ? void 0 : _a.changePassword(user, oldPassword, newPassword);
        });
    }
    /////////////
    // Private //
    /////////////
    getAuthService() {
        switch (this.userAuthServiceType) {
            case AuthServiceType.COGNITO:
                return this.cognitoService;
            case AuthServiceType.OKTA:
                return this.oktaService;
            case AuthServiceType.AUTH0:
                return this.auth0Service;
        }
    }
    /**
     * Checks for existing username on service
     */
    getExistingUserName(service) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // check for active session
                const accessToken = yield (service === null || service === void 0 ? void 0 : service.getUserAccessToken());
                return accessToken && (service === null || service === void 0 ? void 0 : service.getUserName());
            }
            catch (e) {
                // do nothing
            }
        });
    }
    /**
     * Check and set the appropriate authentication service type for a user and its last login info
     * @param {string} username
     */
    updateUserAuthServiceType(username) {
        return __awaiter(this, void 0, void 0, function* () {
            const { authServiceType, isFirstLogin, isUserDeactivated, lastLogin } = yield this.getServiceTypeForUser(username);
            this.userAuthServiceType = authServiceType;
            this.userFirstLogin = isFirstLogin;
            this.userDeactivated = isUserDeactivated;
            this.userLastLogin = lastLogin;
        });
    }
    subscribe(callback) {
        if (this.oktaService) {
            this.oktaService.subscribe(callback);
        }
        if (this.cognitoService) {
            this.cognitoService.subscribe(callback);
        }
        if (this.auth0Service) {
            this.auth0Service.subscribe(callback);
        }
    }
    getAuthServiceType() {
        return this.getAuthService().getAuthServiceType();
    }
}
exports.AuthService = AuthService;
//# sourceMappingURL=authService.js.map